/* eslint-disable react/prop-types */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "./ui/select";
import MobileFormDialog from "./ui/mobile-form-dialog";
import { HHMMInput } from "./ui/hhmm-input";
import { Settings, Plane, Wrench, Replace, Plus, Archive, Trash2, ChevronDown, ChevronUp, Timer } from "lucide-react";
import { toast } from "sonner";
// Shared API base (current host, via the fleet Worker) — see DiAuditPanel.
import { API } from "../App";



const KIND_META = {
  celula:  { label: "Célula",  icon: Plane,    color: "text-sky-300" },
  motor:   { label: "Motor",   icon: Settings, color: "text-amber-300" },
  helice:  { label: "Hélice",  icon: Wrench,   color: "text-emerald-300" },
  outro:   { label: "Outro",   icon: Wrench,   color: "text-slate-300" },
};

const _fmtH = (min) => {
  if (!min && min !== 0) return "—";
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}:${String(m).padStart(2, "0")}`;
};

const _fmtDate = (s) => (s && /^\d{4}-\d{2}-\d{2}/.test(s) ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : (s || "—"));
const _monthsText = (m) => (m % 12 === 0 ? `${m / 12} ${m / 12 === 1 ? "ano" : "anos"}` : `${m} meses`);

// «até 2100:00 de motor (faltam 44:01) ou até 24/10/2026 (faltam 17 dias)»: o limite para substituir o motor
function replaceByParts(rb) {
  const parts = [];
  if (rb?.hours_min != null) {
    parts.push(<span key="h">até <strong>{_fmtH(rb.hours_min)}</strong> de motor{rb.minutes_left != null && rb.minutes_left >= 0 ? <> (faltam <strong>{_fmtH(rb.minutes_left)}</strong>)</> : null}</span>);
  }
  if (rb?.date) {
    parts.push(<span key="d">até <strong>{_fmtDate(rb.date)}</strong>{rb.days_left != null && rb.days_left >= 0 ? <> (faltam <strong>{rb.days_left} dias</strong>)</> : null}</span>);
  }
  return parts.reduce((acc, el, i) => (i ? [...acc, " ou ", el] : [el]), []);
}

// A tarefa «Substituição do motor» deste motor: quando tem de ser trocado (vem do servidor em `potential_status`)
function EnginePotentialSummary({ component }) {
  const ps = component.potential_status;
  const configured = !!(component.potential_hours_min || component.potential_months);
  if (!configured) {
    return (
      <div className="mt-1.5 rounded border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-[11px] text-amber-200" data-testid="di-comp-potential-missing">
        <strong>Potencial por definir.</strong> A substituição do motor é obrigatória: carregue em <em>Editar</em> e indique o potencial (horas e/ou anos) para a aplicação dizer quando o motor tem de ser trocado.
      </div>
    );
  }
  const rb = ps?.replace_by;
  const state = ps?.state;
  const cls = state === "out_of_tolerance" ? "border-red-500/40 bg-red-500/10 text-red-200"
    : state === "in_tolerance" || state === "approaching" ? "border-amber-500/40 bg-amber-500/10 text-amber-200"
    : "border-emerald-500/30 bg-emerald-500/5 text-emerald-200";
  return (
    <div className={`mt-1.5 rounded border px-2 py-1.5 text-[11px] ${cls}`} data-testid="di-comp-potential">
      <div className="text-slate-300">
        Potencial: {[component.potential_hours_min ? `${_fmtH(component.potential_hours_min)} h` : null, component.potential_months ? _monthsText(component.potential_months) : null].filter(Boolean).join(" ou ")}
        {component.potential_start_date ? ` · início ${_fmtDate(component.potential_start_date)}` : ""}
      </div>
      {state === "out_of_tolerance" ? (
        <div><strong>Prazo de substituição terminado.</strong> A aeronave não pode voar até o motor ser substituído.</div>
      ) : rb && (rb.hours_min != null || rb.date) ? (
        <div data-testid="di-comp-replace-by">
          <strong>Substituir o motor</strong> {rb.exact ? "" : "(o mais tardar) "}{replaceByParts(rb)}, o que ocorrer primeiro.
        </div>
      ) : null}
    </div>
  );
}

const POTENTIAL_KEYS = [
  "potential_hours_min", "potential_months", "potential_start_date", "potential_start_hours_min",
  "extension_source", "potential_reached_on", "potential_reached_note",
];

// O que o formulário manda para o servidor no potencial (anos -> meses; vazio -> null)
function potentialBody(form) {
  const years = parseFloat(String(form.potential_years || "").replace(",", "."));
  return {
    potential_hours_min: form.potential_hours_min || null,
    potential_months: Number.isFinite(years) && years > 0 ? Math.round(years * 12) : null,
    potential_start_date: form.potential_start_date || null,
    potential_start_hours_min: form.potential_start_hours_min ?? null,
    extension_source: form.extension_source || "manufacturer",
    potential_reached_on: form.potential_reached_on || null,
    potential_reached_note: (form.potential_reached_note || "").trim() || null,
  };
}

function potentialChanged(form, initial) {
  const now = potentialBody(form);
  const same = (k, a, b) => {
    if (k === "extension_source") return (a || "manufacturer") === (b || "manufacturer");
    if (k === "potential_start_hours_min") return (a || 0) === (b || 0);
    return (a || null) === (b || null);
  };
  return POTENTIAL_KEYS.some((k) => !same(k, initial?.[k], now[k]));
}


export function ComponentForm({ open, onClose, onSaved, aircraftId, initial, isReplace }) {
  const [form, setForm] = useState(() => ({
    kind: initial?.kind || "motor",
    manufacturer: initial?.manufacturer || "",
    model: initial?.model || "",
    serial_number: initial?.serial_number || "",
    installed_at: initial?.installed_at || new Date().toISOString().slice(0, 10),
    pre_install_hours_min: initial?.pre_install_hours_min ?? 0,
    notes: initial?.notes || "",
    reason: "",
    migrate_maintenance: true,
    // Potencial do motor (TBO). Num motor novo (substituição) herda o potencial do antigo; o início e a data da caderneta não
    potential_hours_min: initial?.potential_hours_min ?? null,
    potential_years: initial?.potential_months ? String(initial.potential_months / 12) : "",
    potential_start_date: isReplace ? "" : (initial?.potential_start_date || ""),
    potential_start_hours_min: isReplace ? 0 : (initial?.potential_start_hours_min ?? 0),
    extension_source: initial?.extension_source || "manufacturer",
    potential_reached_on: isReplace ? "" : (initial?.potential_reached_on || ""),
    potential_reached_note: isReplace ? "" : (initial?.potential_reached_note || ""),
    change_reason: "",
  }));
  const [saving, setSaving] = useState(false);
  const [linkedCount, setLinkedCount] = useState(null);

  useEffect(() => {
    if (!isReplace || !initial?.id) return;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/di/aircraft/${aircraftId}/components/${initial.id}/replace-preview`,
          { withCredentials: true }
        );
        setLinkedCount(r.data?.linked_maintenance_count ?? 0);
      } catch (e) { /* silent */ }
    })();
  }, [isReplace, initial?.id, aircraftId]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const isMotor = form.kind === "motor";
  const hadPotential = !!(initial?.potential_hours_min || initial?.potential_months);
  // Alterar um potencial que já estava definido muda o prazo do motor: pede-se o motivo (fica na auditoria)
  const needsReason = !isReplace && !!initial?.id && isMotor && hadPotential && potentialChanged(form, initial);

  const submit = async () => {
    if (needsReason && (form.change_reason || "").trim().length < 3) {
      toast.error("Indique o motivo da alteração do potencial.");
      return;
    }
    setSaving(true);
    try {
      const { potential_years, change_reason, ...rest } = form;  // eslint-disable-line no-unused-vars
      const body = { ...rest };
      if (isMotor) Object.assign(body, potentialBody(form));
      else POTENTIAL_KEYS.forEach((k) => delete body[k]);
      if (!isReplace) {
        delete body.reason;
        delete body.migrate_maintenance;
        if (needsReason) body.reason = form.change_reason.trim();
      }
      const url = isReplace
        ? `${API}/di/aircraft/${aircraftId}/components/${initial.id}/replace`
        : initial?.id
          ? `${API}/di/aircraft/${aircraftId}/components/${initial.id}`
          : `${API}/di/aircraft/${aircraftId}/components`;
      const method = isReplace ? "post" : initial?.id ? "patch" : "post";
      const r = await axios[method](url, body, { withCredentials: true });
      if (isReplace && r.data?.migrated_maintenance_count != null) {
        const n = r.data.migrated_maintenance_count;
        toast.success(
          n > 0
            ? `Componente substituído · ${n} manutenç${n === 1 ? "ão re-ligada" : "ões re-ligadas"} ao novo`
            : "Componente substituído"
        );
      } else {
        toast.success(initial?.id ? "Componente atualizado" : "Componente criado");
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    } finally {
      setSaving(false);
    }
  };

  return (
    <MobileFormDialog
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={isReplace ? `Substituir ${KIND_META[initial?.kind]?.label || ""}` : initial?.id ? "Editar componente" : "Novo componente"}
      description={isReplace ? "O componente actual será arquivado e este passará a ser o activo." : "Identifica o componente para que as horas e manutenções sejam corretamente atribuídas."}
      size="md"
      onSubmit={submit}
      submitLabel={isReplace ? "Substituir" : initial?.id ? "Guardar" : "Criar"}
      submitting={saving}
      testId="di-comp-form-dialog"
    >
      {!initial?.id && !isReplace && (
        <div>
          <Label className="text-slate-300 text-[11px]">Tipo</Label>
          <Select value={form.kind} onValueChange={(v) => setField("kind", v)}>
            <SelectTrigger className="bg-slate-800 border-slate-600 text-slate-100 mt-1" data-testid="di-comp-form-kind">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="celula">Célula (airframe)</SelectItem>
              <SelectItem value="motor">Motor</SelectItem>
              <SelectItem value="helice">Hélice</SelectItem>
              <SelectItem value="outro">Outro</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-slate-300 text-[11px]">Fabricante</Label>
          <Input className="bg-slate-800 border-slate-600 text-slate-100 mt-1" value={form.manufacturer} onChange={(e) => setField("manufacturer", e.target.value)} placeholder="Ex: Rotax" data-testid="di-comp-form-manufacturer" />
        </div>
        <div>
          <Label className="text-slate-300 text-[11px]">Modelo</Label>
          <Input className="bg-slate-800 border-slate-600 text-slate-100 mt-1" value={form.model} onChange={(e) => setField("model", e.target.value)} placeholder="Ex: 912 ULS" data-testid="di-comp-form-model" />
        </div>
      </div>

      <div>
        <Label className="text-slate-300 text-[11px]">Número de série</Label>
        <Input className="bg-slate-800 border-slate-600 text-slate-100 mt-1" value={form.serial_number} onChange={(e) => setField("serial_number", e.target.value)} placeholder="Ex: SN-1234567" data-testid="di-comp-form-sn" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-slate-300 text-[11px]">Data de instalação</Label>
          <Input type="date" className="bg-slate-800 border-slate-600 text-slate-100 mt-1" value={form.installed_at} onChange={(e) => setField("installed_at", e.target.value)} data-testid="di-comp-form-installed-at" />
        </div>
        <div>
          <Label className="text-slate-300 text-[11px]">Horas prévias (HH:mm)</Label>
          <HHMMInput
            value={form.pre_install_hours_min}
            onChange={(mins) => setField("pre_install_hours_min", mins ?? 0)}
            placeholder="0:00"
            data-testid="di-comp-form-pre-hours"
          />
          <div className="text-[10px] text-slate-500 mt-0.5">0:00 se novo. Aceita mais de 99h — ex: 1200:00 = 1200h.</div>
        </div>
      </div>

      {isMotor && (
        <div className="border border-amber-500/30 bg-amber-500/5 rounded p-3 space-y-3" data-testid="di-comp-form-potential">
          <div>
            <div className="text-xs font-semibold text-amber-200">Potencial do motor (TBO)</div>
            <div className="text-[10px] text-slate-400 mt-0.5">
              Indique só o potencial que o fabricante dá ao motor. A aplicação cria a tarefa <strong>«Substituição do motor»</strong> e diz
              em quantos dias e horas o motor tem de ser trocado (o potencial mais a excedência que o fabricante autoriza). É obrigatória.
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-300 text-[11px]">Potencial em horas (HH:mm)</Label>
              <HHMMInput
                value={form.potential_hours_min ?? 0}
                onChange={(mins) => setField("potential_hours_min", mins || null)}
                placeholder="2000:00"
                data-testid="di-comp-form-potential-hours"
              />
              <div className="text-[10px] text-slate-500 mt-0.5">Ex.: Rotax 912 ULS = 2000:00.</div>
            </div>
            <div>
              <Label className="text-slate-300 text-[11px]">Potencial em anos</Label>
              <Input
                type="number" min="0" step="0.5" inputMode="decimal"
                className="bg-slate-800 border-slate-600 text-slate-100 mt-1"
                value={form.potential_years}
                onChange={(e) => setField("potential_years", e.target.value)}
                placeholder="15"
                data-testid="di-comp-form-potential-years"
              />
              <div className="text-[10px] text-slate-500 mt-0.5">Vale o que ocorrer primeiro (horas ou anos).</div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-300 text-[11px]">Início do potencial (data)</Label>
              <Input
                type="date" className="bg-slate-800 border-slate-600 text-slate-100 mt-1"
                value={form.potential_start_date}
                onChange={(e) => setField("potential_start_date", e.target.value)}
                data-testid="di-comp-form-potential-start"
              />
              <div className="text-[10px] text-slate-500 mt-0.5">A data da primeira caderneta do motor (ou da última revisão geral). Obrigatória se indicar anos.</div>
            </div>
            <div>
              <Label className="text-slate-300 text-[11px]">Horas do motor nessa data (HH:mm)</Label>
              <HHMMInput
                value={form.potential_start_hours_min ?? 0}
                onChange={(mins) => setField("potential_start_hours_min", mins ?? 0)}
                placeholder="0:00"
                data-testid="di-comp-form-potential-start-hours"
              />
              <div className="text-[10px] text-slate-500 mt-0.5">0:00 se o motor era novo.</div>
            </div>
          </div>
          <div>
            <Label className="text-slate-300 text-[11px]">Quem autoriza exceder o potencial</Label>
            <div className="mt-1 space-y-1 text-[11px] text-slate-200">
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="radio" name="ext-source" checked={form.extension_source !== "cti"} onChange={() => setField("extension_source", "manufacturer")} className="mt-0.5" data-testid="di-comp-form-ext-manufacturer" />
                <span><strong>O fabricante</strong> (Rotax: 5 % das horas ou 6 meses, o que ocorrer primeiro). É o que vale sempre que o fabricante diz alguma coisa.</span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="radio" name="ext-source" checked={form.extension_source === "cti"} onChange={() => setField("extension_source", "cti")} className="mt-0.5" data-testid="di-comp-form-ext-cti" />
                <span><strong>A CTI 05-04 do INAC</strong> (até 10 %): só se o fabricante não disser nada.</span>
              </label>
            </div>
          </div>
          <details open={!!(form.potential_reached_on || initial?.potential_status?.extension?.reached)} className="text-[11px] text-slate-300">
            <summary className="cursor-pointer text-slate-200">Data em que o potencial foi atingido (caderneta do motor)</summary>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Input
                  type="date" className="bg-slate-800 border-slate-600 text-slate-100"
                  value={form.potential_reached_on}
                  onChange={(e) => setField("potential_reached_on", e.target.value)}
                  data-testid="di-comp-form-reached-on"
                />
                <div className="text-[10px] text-slate-500 mt-0.5">
                  Deixe vazio para a aplicação calcular pelos voos registados
                  {initial?.potential_status?.extension?.reached_on_flights ? ` (dão ${_fmtDate(initial.potential_status.extension.reached_on_flights)})` : ""}.
                  Se indicar, a da caderneta manda.
                </div>
              </div>
              <div>
                <Input
                  className="bg-slate-800 border-slate-600 text-slate-100"
                  value={form.potential_reached_note}
                  onChange={(e) => setField("potential_reached_note", e.target.value)}
                  placeholder="Ex.: registo 5/321"
                  maxLength={200}
                  data-testid="di-comp-form-reached-note"
                />
                <div className="text-[10px] text-slate-500 mt-0.5">O registo da caderneta que o prova.</div>
              </div>
            </div>
          </details>
          {needsReason && (
            <div>
              <Label className="text-amber-200 text-[11px]">Motivo da alteração do potencial (obrigatório)</Label>
              <Input
                className="bg-slate-800 border-amber-500/40 text-slate-100 mt-1"
                value={form.change_reason}
                onChange={(e) => setField("change_reason", e.target.value)}
                placeholder="Ex.: confirmado no manual do fabricante"
                data-testid="di-comp-form-change-reason"
              />
              <div className="text-[10px] text-slate-500 mt-0.5">Fica registado, com o seu nome, no histórico da tarefa «Substituição do motor».</div>
            </div>
          )}
        </div>
      )}

      {isReplace && (
        <>
          <div>
            <Label className="text-slate-300 text-[11px]">Motivo da substituição</Label>
            <Input className="bg-slate-800 border-slate-600 text-slate-100 mt-1" value={form.reason} onChange={(e) => setField("reason", e.target.value)} placeholder="Ex: substituição programada às 2000h" data-testid="di-comp-form-reason" />
          </div>
          {linkedCount !== null && linkedCount > 0 && (
            <div className="border border-amber-500/40 bg-amber-500/5 rounded p-2.5">
              <div className="text-[11px] text-amber-200 font-semibold mb-1.5">
                ⚠ {linkedCount} manutenç{linkedCount === 1 ? "ão tem" : "ões têm"} este componente ligado
              </div>
              <label className="flex items-start gap-2 cursor-pointer text-[11px] text-slate-200" data-testid="di-comp-form-migrate-label">
                <input
                  type="checkbox"
                  checked={!!form.migrate_maintenance}
                  onChange={(e) => setField("migrate_maintenance", e.target.checked)}
                  className="mt-0.5"
                  data-testid="di-comp-form-migrate"
                />
                <span>
                  Re-ligar essas manutenções ao <strong>novo</strong> componente e reiniciar o ciclo (last_done = 0).
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    Se desligares, as manutenções vão continuar a apontar para o componente arquivado e contar horas a partir da aeronave total (fallback).
                  </div>
                </span>
              </label>
            </div>
          )}
        </>
      )}

      <div>
        <Label className="text-slate-300 text-[11px]">Notas</Label>
        <Input className="bg-slate-800 border-slate-600 text-slate-100 mt-1" value={form.notes} onChange={(e) => setField("notes", e.target.value)} data-testid="di-comp-form-notes" />
      </div>
    </MobileFormDialog>
  );
}


// ─── Ground Run Time dialog ───────────────────────────────────────────────

function GroundRunDialog({ component, aircraftId, onClose, onSaved }) {
  const [groundRunMinutes, setGroundRunMinutes] = useState(component.ground_run_minutes || 0);
  const [notes, setNotes] = useState(component.notes || "");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/di/aircraft/${aircraftId}/components/${component.id}/ground-run`,
        { ground_run_minutes: groundRunMinutes, notes: notes || null },
        { withCredentials: true }
      );
      toast.success(`Tempo em solo atualizado: ${_fmtH(groundRunMinutes)}`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a guardar");
    } finally {
      setSaving(false);
    }
  };

  const meta = KIND_META[component.kind] || KIND_META.outro;
  const Icon = meta.icon;

  return (
    <MobileFormDialog
      open
      title={
        <div className="flex items-center gap-2">
          <Timer className="w-4 h-4 text-amber-300" />
          <span>Tempo de Motor em Solo — {[component.manufacturer, component.model].filter(Boolean).join(" ") || "Motor"}</span>
        </div>
      }
      onOpenChange={(o) => { if (!o) onClose(); }}
      onSubmit={submit}
      submitLabel={saving ? "A guardar…" : "Guardar"}
      saving={saving}
    >
      <div className="space-y-4">
        <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 text-[11px] text-amber-300/80">
          O tempo em solo acumula ao tempo de voo e contribui para o total de horas do motor.
          Introduz o total acumulado desde a instalação deste motor.
        </div>

        {/* Current hours summary — 3 clear numbers */}
        <div className="grid grid-cols-3 gap-2 text-center text-[11px]">
          <div className="rounded border border-amber-500/30 bg-amber-500/5 p-2">
            <div className="text-amber-400 text-[9px] uppercase mb-0.5">Solo (actual)</div>
            <div className="font-mono text-amber-300 font-semibold">{_fmtH((component.flight_ground_run_min || 0) + (component.ground_run_minutes || 0))}</div>
          </div>
          <div className="rounded border border-blue-500/20 bg-blue-500/5 p-2">
            <div className="text-blue-400/70 text-[9px] uppercase mb-0.5">Voo</div>
            <div className="font-mono text-blue-200 font-semibold">{_fmtH(component.flight_hours_min || 0)}</div>
          </div>
          <div className="rounded border border-emerald-500/30 bg-emerald-500/5 p-2">
            <div className="text-emerald-400/70 text-[9px] uppercase mb-0.5">Total motor</div>
            <div className="font-mono text-emerald-300 font-bold">{_fmtH(component.current_hours_min || 0)}</div>
          </div>
        </div>

        <div>
          <label className="text-[11px] text-slate-300 font-medium block mb-1">
            Novo total de tempo em solo (HH:mm)
          </label>
          <HHMMInput
            value={groundRunMinutes}
            onChange={(mins) => setGroundRunMinutes(mins ?? 0)}
            placeholder="0:00"
            data-testid="di-ground-run-input"
          />
          <p className="text-[10px] text-slate-500 mt-1">
            Total acumulado desde a instalação deste motor ({component.installed_at}).
            {" "}Aceita mais de 99h — ex: 120:30 = 120h30m.
          </p>
        </div>

        <div>
          <label className="text-[11px] text-slate-300 font-medium block mb-1">Observação (opcional)</label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Ex: Calibração 10-06-2026 · verificação motor"
            className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-slate-100 text-sm"
          />
        </div>
      </div>
    </MobileFormDialog>
  );
}


export default function ComponentsManager({ aircraftId, registration }) {
  const [items, setItems] = useState([]);
  const [archived, setArchived] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [replacing, setReplacing] = useState(null);
  const [groundRunFor, setGroundRunFor] = useState(null);

  const reload = useCallback(async () => {
    if (!aircraftId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/di/aircraft/${aircraftId}/components?include_archived=true`, { withCredentials: true });
      const all = r.data || [];
      setItems(all.filter((c) => !c.archived));
      setArchived(all.filter((c) => c.archived));
    } catch (e) {
      toast.error("Erro a carregar componentes");
    } finally { setLoading(false); }
  }, [aircraftId]);

  useEffect(() => { reload(); }, [reload]);

  const onDelete = async (c) => {
    if (!window.confirm(`Apagar ${KIND_META[c.kind]?.label || c.kind} ${c.model || ""}? Esta acção é definitiva. Para histórico de substituições, usa antes "Substituir".`)) return;
    try {
      await axios.delete(`${API}/di/aircraft/${aircraftId}/components/${c.id}`, { withCredentials: true });
      toast.success("Componente apagado");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    }
  };

  if (!aircraftId) {
    return (
      <div className="text-[11px] text-slate-500 italic">
        Componentes (motor, célula, hélice) podem ser adicionados após criar a aeronave.
      </div>
    );
  }

  return (
    <div className="border border-slate-700 rounded p-3 bg-slate-900/40 min-w-0" data-testid="di-components-manager">
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="text-xs font-semibold text-slate-200 flex items-center gap-1.5 min-w-0">
          <Settings className="w-4 h-4 text-amber-300" /> Componentes track-hours
        </div>
        <Button size="sm" onClick={() => setCreating(true)} className="h-7 bg-blue-600 hover:bg-blue-500 text-[11px]" data-testid="di-components-add-btn">
          <Plus className="w-3 h-3 mr-1" /> Adicionar
        </Button>
      </div>

      {loading ? (
        <div className="text-[11px] text-slate-500">A carregar…</div>
      ) : items.length === 0 ? (
        <div className="text-[11px] text-slate-500 italic">
          Sem componentes registados. Adiciona o motor (com o potencial, para a aplicação controlar a substituição do motor), a célula e a hélice, para que as horas sejam contadas independentemente.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((c) => {
            const meta = KIND_META[c.kind] || KIND_META.outro;
            const Icon = meta.icon;
            return (
              <div key={c.id} className="border border-slate-800 rounded p-2 bg-slate-900/60 min-w-0" data-testid="di-component-item">
                <div className="flex items-start gap-2 min-w-0">
                  <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${meta.color}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap min-w-0">
                      <span className={`text-[10px] uppercase font-bold tracking-wide ${meta.color}`}>{meta.label}</span>
                      <span className="text-xs text-slate-100 font-medium truncate min-w-0">
                        {(c.manufacturer || c.model) ? `${c.manufacturer || ""} ${c.model || ""}`.trim() : "—"}
                      </span>
                    </div>
                    {c.serial_number && (
                      <div className="text-[10px] text-slate-400 truncate">SN: {c.serial_number}</div>
                    )}
                    {/* Hours breakdown */}
                    {c.kind === "motor" ? (() => {
                      const soloMin = (c.flight_ground_run_min || 0) + (c.ground_run_minutes || 0);
                      const vooMin = (c.pre_install_hours_min || 0) + (c.flight_hours_min || 0);
                      const preMin = c.pre_install_hours_min || 0;
                      const totalMin = c.current_hours_min || 0;
                      return (
                        <div className="mt-1.5 space-y-1">
                          {/* 3 números principais */}
                          <div className="grid grid-cols-3 gap-1 text-center">
                            <div className="rounded border border-amber-500/20 bg-amber-500/5 px-1 py-1">
                              <div className="text-[8px] uppercase tracking-wide text-amber-400/70 mb-0.5">Solo</div>
                              <div className="font-mono text-[11px] text-amber-200 font-semibold">{_fmtH(soloMin)}</div>
                            </div>
                            <div className="rounded border border-blue-500/20 bg-blue-500/5 px-1 py-1">
                              <div className="text-[8px] uppercase tracking-wide text-blue-400/70 mb-0.5">Voo</div>
                              <div className="font-mono text-[11px] text-blue-200 font-semibold">{_fmtH(vooMin)}</div>
                            </div>
                            <div className="rounded border border-emerald-500/30 bg-emerald-500/8 px-1 py-1">
                              <div className="text-[8px] uppercase tracking-wide text-emerald-400/70 mb-0.5">Total motor</div>
                              <div className="font-mono text-[11px] text-emerald-300 font-bold">{_fmtH(totalMin)}</div>
                            </div>
                          </div>
                          {/* Contexto: data instalação */}
                          <div className="text-[9px] text-slate-500">
                            desde {c.installed_at}
                          </div>
                          <EnginePotentialSummary component={c} />
                        </div>
                      );
                    })() : (
                      <div className="text-[11px] text-emerald-300 font-mono mt-0.5">
                        {_fmtH(c.current_hours_min)} <span className="text-slate-500 text-[10px]">desde {c.installed_at}</span>
                      </div>
                    )}
                    {c.notes && <div className="text-[10px] text-slate-400 italic mt-0.5 break-words">{c.notes}</div>}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-1 mt-2">
                  <Button size="sm" variant="outline" onClick={() => setEditing(c)} className="h-6 text-[10px] border-slate-600 text-slate-300" data-testid="di-component-edit-btn">Editar</Button>
                  {c.kind === "motor" && (
                    <Button size="sm" variant="outline" onClick={() => setGroundRunFor(c)} className="h-6 text-[10px] border-amber-600/40 text-amber-300 hover:bg-amber-500/10" data-testid="di-component-ground-run-btn">
                      <Timer className="w-3 h-3 mr-1" /> Solo
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => setReplacing(c)} className="h-6 text-[10px] border-amber-600/40 text-amber-300 hover:bg-amber-500/10" data-testid="di-component-replace-btn">
                    <Replace className="w-3 h-3 mr-1" /> Substituir
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => onDelete(c)} className="h-6 text-[10px] text-red-400 hover:bg-red-500/10 hover:text-red-300" data-testid="di-component-delete-btn">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {archived.length > 0 && (
        <div className="mt-3 pt-2 border-t border-slate-800">
          <button
            type="button"
            className="text-[11px] text-slate-400 hover:text-slate-200 flex items-center gap-1"
            onClick={() => setShowArchived((v) => !v)}
            data-testid="di-components-toggle-archived"
          >
            <Archive className="w-3 h-3" />
            {showArchived ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            Histórico ({archived.length})
          </button>
          {showArchived && (
            <div className="mt-2 space-y-1 text-[10px] text-slate-500" data-testid="di-components-archived-list">
              {archived.map((c) => (
                <div key={c.id} className="flex items-center gap-2 min-w-0">
                  <Archive className="w-3 h-3 shrink-0" />
                  <span className="truncate">
                    {KIND_META[c.kind]?.label || c.kind}: {[c.manufacturer, c.model, c.serial_number].filter(Boolean).join(" · ")}
                    <span className="text-slate-600"> · {c.installed_at} → {c.archived_at?.slice(0, 10) || "?"}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {creating && (
        <ComponentForm
          open
          aircraftId={aircraftId}
          onClose={() => setCreating(false)}
          onSaved={reload}
        />
      )}
      {editing && (
        <ComponentForm
          open
          aircraftId={aircraftId}
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={reload}
        />
      )}
      {replacing && (
        <ComponentForm
          open
          isReplace
          aircraftId={aircraftId}
          initial={replacing}
          onClose={() => setReplacing(null)}
          onSaved={reload}
        />
      )}
      {groundRunFor && (
        <GroundRunDialog
          component={groundRunFor}
          aircraftId={aircraftId}
          onClose={() => setGroundRunFor(null)}
          onSaved={reload}
        />
      )}
    </div>
  );
}
